/**
 * src/gm/kernel/game-master.ts — GameMaster 对外门面（设计稿 §4/§5/§6）。
 *
 * 与旧版的本质差异：不暴露 startXxx 直调方法，一切意图经 enqueue 入唤醒队列，
 * drain 认领执行；门禁停锚/解除产 gate 事件；阶段执行走 session 化 runner。
 * GameMaster 只做编排与状态：不拼 prompt、不算数、不直接渲染。
 *
 * 偏差登记（收工报守门员）：WakeupAction 较设计稿 §4.1 枚举多 'publishDefective'——
 * 事故三选的"残次品上架"是 Phase A 既有功能（旧版同名方法），设计稿枚举漏列，
 * 实现补上并在记忆文件与附录 B 提案中登记，不视为新功能。
 */

import { ApiClient } from '../employees/api-client'
import {
  parseCodeBlock,
  parseDelimited,
  parseStructured,
  validateArt,
  validateDecisions,
  validateIndexHtml,
  validateReviewSub,
  type ArtPayload,
  type PlanPayload,
  type ReviewSubscore,
} from '../employees/parser'
import {
  PLAN_RESPONSE_FORMAT,
  REVIEW_RESPONSE_FORMAT,
  buildArtMessages,
  buildCodeMessages,
  buildPlanMessages,
  buildReviewMessages,
} from '../employees/prompt-builder'
import { EmployeeRunner } from '../employees/runner'
import {
  archiveProjectSummary,
  audit,
  clearProjectEvents,
  emitError,
  emitPhase,
  gateEvent,
  narrate,
  replayEvents,
  warn,
  wakeupEvent,
  type GmPhase,
} from '../events/event-log'
import { readArtifact, writeArtifact, clearArtifacts, isNearCapacity, removeArtifact, type ArtifactKind } from '../platform/artifacts'
import { MAX_VALIDATE_ROUNDS, REVIEWER_PEN_NAME_KEY } from '../platform/config'
import { GmError, ParseError, isQuotaExceeded } from '../platform/errors'
import { gmLockName, withProjectLock } from '../platform/locks'
import { modelFor, REVIEWER_CHOICE } from '../platform/model-map'
import { readJson, writeJson } from '../platform/storage'
import type {
  Project,
  SaveBundle,
  SaveStorePort,
  TokenUsage,
} from '../platform/save-types'
import { smokeTest, summarizeSmokeFailure, type SmokeReport } from '../sandbox/smoke-probe'
import { clearProjectSessions, clearSession, loadSession } from '../session/session-store'
import type { SessionRecord, StageKind } from '../session/session-codec'
import {
  buildReview,
  clearPendingSettlement,
  readPendingSettlement,
  settle,
  type PendingSettlement,
  type SettleResult,
} from '../settlement/settle'
import {
  allDecisionsResolved,
  assertStatus,
  findProject,
  findTeamEmployee,
  resolvedDecisionsOf,
} from './state-machine'
import {
  WakeupQueue,
  type WakeupAction,
  type WakeupRequest,
  type WakeupSource,
} from './wakeup-queue'

export interface GameMasterDeps {
  /** 存档端口（外壳实现，M3；测试注入内存替身） */
  store: SaveStorePort
  api?: ApiClient
  /** 冒烟台架（测试可注入替身） */
  smoke?: (html: string) => Promise<SmokeReport>
}

// ── 评审员笔名（同季度同一支笔）──────────────────────────────────────────────

interface PenNameRecord {
  season: string
  name: string
}

function readPenName(season: string): string | null {
  const rec = readJson<PenNameRecord>(REVIEWER_PEN_NAME_KEY)
  return rec && rec.season === season ? rec.name : null
}

function writePenName(season: string, name: string): void {
  try {
    writeJson(REVIEWER_PEN_NAME_KEY, { season, name })
  } catch {
    // 忽略
  }
}

// ── 恢复报告 ─────────────────────────────────────────────────────────────────

export interface ResumeReport {
  settlementResumed: SettleResult | null
  project: Project | null
  action:
    | 'none'
    | 'settlement-resumed'
    | 'wait-start'
    | 'await-decision'
    | 'continue-dev'
    | 'accepted'
    | 'wait-publish'
  message: string
}

export interface RunStateInfo {
  phase: GmPhase
  queue: WakeupRequest[]
  activeSession: Pick<SessionRecord, 'stage' | 'rounds' | 'usage' | 'state'> & { partialChars: number } | null
}

const STAGES: StageKind[] = ['plan', 'art', 'code', 'review']

export class GameMaster {
  private readonly store: SaveStorePort
  private readonly runner: EmployeeRunner
  private readonly smoke: (html: string) => Promise<SmokeReport>
  private readonly queue: WakeupQueue

  constructor(deps: GameMasterDeps) {
    this.store = deps.store
    this.runner = new EmployeeRunner(deps.api ?? new ApiClient())
    this.smoke = deps.smoke ?? smokeTest
    this.queue = new WakeupQueue((req, transition) =>
      wakeupEvent(req.projectId, req.id, req.action, transition),
    )
  }

  // ── 唤醒入口（设计稿 §8.1：enqueue 是唯一动作入口）─────────────────────────

  async enqueue(
    action: WakeupAction,
    projectId: string,
    source: WakeupSource = 'player',
  ): Promise<WakeupRequest> {
    const bundle = await this.store.readAll()
    const { request, isNew } = this.queue.enqueue(action, projectId, source, () =>
      this.validateAction(action, bundle, projectId),
    )
    if (!isNew) return request // 合并：双击/重复入队，返回既有请求
    return this.drain(projectId)
  }

  /** 认领并执行下一条 pending；执行期 BAD_STATUS → deferred（前置已变化），空转时重估 */
  private async drain(projectId: string): Promise<WakeupRequest> {
    const req = this.queue.nextPending(projectId)
    if (!req) throw new GmError('BAD_STATUS', '队列里没有待执行的指令。')
    return withProjectLock(gmLockName(projectId), async () => {
      this.queue.transition(req.id, 'claimed')
      try {
        await this.execute(req)
        this.queue.transition(req.id, 'completed')
      } catch (err) {
        emitPhase(projectId, 'idle')
        if (err instanceof GmError && err.code === 'BAD_STATUS') {
          // 执行中发现前置已变化（状态机已越过）→ 推迟，待队列空转重估
          this.queue.transition(req.id, 'deferred', { reason: err.message })
          await this.reevaluateStuck(projectId)
          return req
        }
        this.queue.transition(req.id, 'failed', {
          reason: err instanceof Error ? err.message : String(err),
        })
        if (err instanceof GmError) emitError(projectId, err.code, err.message)
        throw err
      }
      // 队列空转：重估被推迟的意图
      await this.reevaluateStuck(projectId)
      return req
    })
  }

  /** deferred/卡死 claimed 的重估（设计稿 §4.2 推迟语义 + §6 ②）：合法回 pending 并驱动，非法 failed */
  private async reevaluateStuck(projectId?: string): Promise<void> {
    for (const req of this.queue.stuckRequests(projectId)) {
      const bundle = await this.store.readAll()
      try {
        this.validateAction(req.action, bundle, req.projectId)
      } catch {
        this.queue.transition(req.id, 'failed', { reason: '重估时前置条件已不存在' })
        warn(req.projectId, '一条被推迟的指令已失效（项目状态已越过），不再执行。')
        continue
      }
      this.queue.transition(req.id, 'pending')
      try {
        await this.drain(req.projectId)
      } catch {
        // drain 内部的失败已落 failed + error 事件；重估继续处理下一条
      }
    }
  }

  // ── 入队前置校验（非法动作当场拒绝）──────────────────────────────────────

  private validateAction(action: WakeupAction, bundle: SaveBundle, projectId: string): void {
    const project = findProject(bundle, projectId)
    switch (action) {
      case 'startPlanning':
        assertStatus(project, 'drafting', '开工')
        findTeamEmployee(bundle, project, 'designer')
        findTeamEmployee(bundle, project, 'programmer')
        findTeamEmployee(bundle, project, 'artist')
        return
      case 'startDevelopment':
        assertStatus(project, 'developing', '开始开发')
        if (!allDecisionsResolved(bundle, project)) {
          throw new GmError('BAD_STATUS', '还有决策没拍板——程序与美术不能开工。')
        }
        return
      case 'accept':
        assertStatus(project, 'developing', '验收')
        return
      case 'publish':
        assertStatus(project, 'in_review', '上架')
        return
      case 'publishDefective':
        assertStatus(project, 'developing', '残次品上架')
        return
      case 'abandon':
        if (project.status === 'completed') {
          throw new GmError('BAD_STATUS', '这盘卡带已经结算封盘了，没有放弃一说。')
        }
        return
    }
  }

  private async execute(req: WakeupRequest): Promise<void> {
    switch (req.action) {
      case 'startPlanning':
        return this.doStartPlanning(req.projectId)
      case 'startDevelopment':
        return this.doStartDevelopment(req.projectId)
      case 'accept':
        return this.acceptInternal(req.projectId)
      case 'publish':
        await this.doPublish(req.projectId)
        return
      case 'publishDefective':
        return this.doPublishDefective(req.projectId)
      case 'abandon':
        return this.doAbandon(req.projectId)
    }
  }

  // ── §3 开工（drafting → awaiting_decision）──────────────────────────────

  private async doStartPlanning(projectId: string): Promise<void> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    assertStatus(project, 'drafting', '开工')
    const designer = findTeamEmployee(bundle, project, 'designer')
    findTeamEmployee(bundle, project, 'programmer')
    findTeamEmployee(bundle, project, 'artist')

    emitPhase(projectId, 'planning', `${designer.name} 裁剪 idea 中`)
    narrate(projectId, designer.name, `「${designer.catchphrase}」——idea 我拿到了，先去把花活砍干净。`)
    let out
    try {
      out = await this.runner.runStage<PlanPayload>({
        projectId,
        stage: 'plan',
        stageLabel: designer.name,
        employeeId: designer.id,
        choice: modelFor(designer.rarity, 'plan'),
        buildMessages: () => buildPlanMessages(designer, project, bundle.market),
        responseFormat: PLAN_RESPONSE_FORMAT,
        parse: (raw) => validateDecisions(parseStructured(raw), project, designer.id),
      })
    } catch (err) {
      if (err instanceof ParseError) {
        throw new GmError(
          'DESIGN_INVALID',
          `${designer.name} 交上来的东西没法用：${err.issues[0]}。要重派一次，还是放弃这个项目？`,
          err.issues,
        )
      }
      throw err
    }
    const { payload, usage } = out

    // 落盘（一次批量写内按序）：决策 push + 项目回填 + 三员工 in_project + 策划 token
    const decisions = [...bundle.decisions.decisions, ...payload.decisions]
    const tokenUsage: TokenUsage = {
      designer: project.token_usage.designer + usage.total_tokens,
      programmer: project.token_usage.programmer,
      artist: project.token_usage.artist,
      total: 0,
    }
    tokenUsage.total = tokenUsage.designer + tokenUsage.programmer + tokenUsage.artist
    const projects = bundle.projects.projects.map((p) =>
      p.id === projectId
        ? {
            ...p,
            decision_ids: payload.decisions.map((d) => d.id),
            status: 'awaiting_decision' as const,
            token_usage: tokenUsage,
          }
        : p,
    )
    const teamIds = new Set([project.team.designer, project.team.programmer, project.team.artist])
    const employees = bundle.employees.employees.map((e) =>
      teamIds.has(e.id) ? { ...e, status: 'in_project' as const } : e,
    )
    await this.writeBatchSafe(projectId, {
      decisions: { decisions },
      projects: { projects },
      employees: { employees },
    })
    audit(projectId, 'decisions', `+${payload.decisions.length} 条 pending 决策`, '策划交付决策请求')
    audit(projectId, 'projects', `${projectId}.status → awaiting_decision`, '策划阶段完成，等玩家拍板')
    audit(projectId, 'employees', '团队三员工 status → in_project', '开工置位（结算或放弃时归还）')
    if (payload.design_doc.trim()) {
      this.writeArtifactAudited(projectId, 'design-doc', payload.design_doc)
    }

    // 门禁停锚：开工前决策拍板（等外壳写回 + enqueue startDevelopment 续跑）
    gateEvent(projectId, 'decision-batch@pre-dev', 'enter', {
      decisionIds: payload.decisions.map((d) => d.id),
    })
    emitPhase(projectId, 'idle')
    narrate(
      projectId,
      designer.name,
      `「${designer.catchphrase}」——送来了 ${payload.decisions.length} 份决策请求，老板，去项目面板拍板吧。`,
    )
  }

  // ── §4 开始开发（developing → in_review）────────────────────────────────

  private async doStartDevelopment(projectId: string): Promise<void> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    assertStatus(project, 'developing', '开始开发')
    const resolved = resolvedDecisionsOf(bundle, project)
    if (resolved.length !== project.decision_ids.length) {
      throw new GmError('BAD_STATUS', '还有决策没拍板——程序与美术不能开工。')
    }
    const artist = findTeamEmployee(bundle, project, 'artist')
    const programmer = findTeamEmployee(bundle, project, 'programmer')
    gateEvent(projectId, 'decision-batch@pre-dev', 'exit')

    // ① 美术先行（串行：程序必须读到美术成品规格；幂等：产物已在 → 跳过，恢复路径不重复烧 token）
    let tokenUsage = project.token_usage
    if (!readArtifact(projectId, 'art-spec')?.trim()) {
      emitPhase(projectId, 'art', `${artist.name} 定视觉规格中`)
      narrate(projectId, artist.name, `「${artist.catchphrase}」`)
      let art
      try {
        art = await this.runner.runStage<ArtPayload>({
          projectId,
          stage: 'art',
          stageLabel: artist.name,
          employeeId: artist.id,
          choice: modelFor(artist.rarity, 'art'),
          buildMessages: () => buildArtMessages(artist, project, resolved),
          parse: (raw) => {
            const parts = parseDelimited(raw, ['===ART_SPEC===', '===ASSETS_CSS===', '===END==='])
            return validateArt(parts['===ART_SPEC==='], parts['===ASSETS_CSS==='])
          },
        })
      } catch (err) {
        if (err instanceof ParseError) {
          throw new GmError(
            'ART_INVALID',
            `${artist.name} 交上来的东西没法用：${err.issues[0]}。要重派一次，还是放弃这个项目？`,
            err.issues,
          )
        }
        throw err
      }
      this.writeArtifactAudited(projectId, 'art-spec', art.payload.artSpec)
      this.writeArtifactAudited(projectId, 'assets-css', art.payload.assetsCss)
      tokenUsage = { ...tokenUsage, artist: tokenUsage.artist + art.usage.total_tokens }
      tokenUsage.total = tokenUsage.designer + tokenUsage.programmer + tokenUsage.artist
      await this.patchTokenUsage(projectId, tokenUsage)
    }

    // ② 程序随后（流式；校验反馈 loop；重试耗尽 → 开发事故三选）
    emitPhase(projectId, 'coding', `${programmer.name} 写卡带中`)
    narrate(projectId, programmer.name, `「${programmer.catchphrase}」——美术规格我读完了，开工。`)
    let html: string
    try {
      // 事故情报一次性消费（M5-B1）：再派一轮时上一版死因进新会话，随后删除情报本体
      const lastFailure = readArtifact(projectId, 'last-failure')
      if (lastFailure) removeArtifact(projectId, 'last-failure')
      const code = await this.runner.runStage<string>({
        projectId,
        stage: 'code',
        stageLabel: programmer.name,
        employeeId: programmer.id,
        choice: modelFor(programmer.rarity, 'code'),
        buildMessages: () =>
          buildCodeMessages(
            programmer,
            project,
            resolved,
            readArtifact(projectId, 'art-spec') ?? '',
            readArtifact(projectId, 'assets-css') ?? '',
            readArtifact(projectId, 'design-doc'),
            lastFailure,
          ),
        stream: true,
        parse: (raw) => validateIndexHtml(parseCodeBlock(raw, 'html')),
        extractPartial: (raw) => raw,
      })
      html = code.payload
      tokenUsage = { ...tokenUsage, programmer: tokenUsage.programmer + code.usage.total_tokens }
      tokenUsage.total = tokenUsage.designer + tokenUsage.programmer + tokenUsage.artist
      await this.patchTokenUsage(projectId, tokenUsage)
    } catch (err) {
      if (err instanceof ParseError) {
        // 开发事故：尽量把残次品留在产物区（玩家可选"残次品上架"——评审会给事故分）
        const raw = err.raw
        if (raw) {
          try {
            this.writeArtifactAudited(projectId, 'index-html', parseCodeBlock(raw, 'html'))
          } catch {
            // 残次品都提取不出来就算了
          }
        }
        // 事故情报落产物区（M5-B1：再派一轮时喂回程序员）+ 旧会话归档删除（新一轮重开）
        this.writeArtifactAudited(
          projectId,
          'last-failure',
          `返工 ${MAX_VALIDATE_ROUNDS} 轮后交付仍不合格，问题清单：\n` +
            err.issues.map((s, i) => `${i + 1}. ${s}`).join('\n'),
        )
        clearSession(projectId, 'code')
        gateEvent(projectId, 'accident-triage@on-failure', 'enter', { issues: err.issues })
        throw new GmError(
          'DEV_ACCIDENT',
          `${programmer.name} 返工三轮还是点不亮这盘卡带。老板，三选一：再派一轮 / 残次品上架（评审会给事故分）/ 放弃项目。`,
          { issues: err.issues, raw: err.raw },
        )
      }
      throw err
    }
    this.writeArtifactAudited(projectId, 'index-html', html)

    // ③ 验收（§4.3）
    await this.acceptInternal(projectId)
  }

  /** §4.3 验收：冒烟 → output_path 回填 → status → in_review */
  private async acceptInternal(projectId: string): Promise<void> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    assertStatus(project, 'developing', '验收')
    const html = readArtifact(projectId, 'index-html')
    if (!html || !html.trim()) {
      throw new GmError('MISSING_ARTIFACT', '产物区里找不到成品卡带——让程序再跑一轮吧。')
    }
    emitPhase(projectId, 'acceptance', '冒烟验收中')
    const report = await this.smoke(html)
    audit(projectId, `tp3:artifact:${projectId}:index-html`, `冒烟 verdict=${report.verdict ?? 'BLOCKED(探针未存活)'}`, '验收冒烟台架')
    // 误报防护：探针被 CSP/沙箱整体拦截时 verdict=null——这是部署环境的问题，
    // 不是游戏的死法，绝不能派员工返工（设计稿 §9.6）。
    if (report.verdict === null) {
      throw new GmError(
        'SMOKE_ENV_BLOCKED',
        '冒烟探针没跑起来——运行环境的安全策略把沙箱脚本整体拦截了，机器无法判定成品质量。请检查部署安全头。',
        report,
      )
    }
    if (report.verdict !== 'PASS') {
      // 事故情报落产物区（M5-B1：再派一轮时程序员能看到上一轮死因）+
      // 旧 code 会话归档删除（验收失败时它已封盘，再派一轮必须重开新会话）
      this.writeArtifactAudited(
        projectId,
        'last-failure',
        `验收冒烟 verdict=FAIL，死因清单：\n` +
          (report.errors.length
            ? report.errors.map((s, i) => `${i + 1}. ${s}`).join('\n')
            : '（探针未捕获明细）'),
      )
      clearSession(projectId, 'code')
      gateEvent(projectId, 'accident-triage@on-failure', 'enter', { smoke: report.errors[0] ?? null })
      // E2（deck §九终稿模板，逐字）：toast 只出人话摘要——英文原文/堆栈完整进控制台
      console.error('[gm] 验收冒烟 FAIL 原文：', report.errors)
      throw new GmError(
        'DEV_ACCIDENT',
        `验收冒烟没跑起来——${summarizeSmokeFailure(report)}。老板，三选一：再派一轮 / 残次品上架 / 放弃项目。`,
        report,
      )
    }
    const projects = bundle.projects.projects.map((p) =>
      p.id === projectId
        ? { ...p, output_path: `games/${projectId}/index.html`, status: 'in_review' as const }
        : p,
    )
    await this.writeBatchSafe(projectId, { projects: { projects } })
    audit(projectId, 'projects', `${projectId}.status → in_review，output_path 回填`, '开发验收通过')
    emitPhase(projectId, 'idle')
    const artist = findTeamEmployee(bundle, project, 'artist')
    const programmer = findTeamEmployee(bundle, project, 'programmer')
    narrate(projectId, programmer.name, `「${programmer.catchphrase}」——验收过了，可以上架。`)
    narrate(projectId, artist.name, `「${artist.catchphrase}」`)
    // 门禁停锚：上架拍板
    gateEvent(projectId, 'publish-confirm@pre-review', 'enter')
  }

  /** 残次品上架（开发事故三选之一）：跳过验收直接进评审，评审会给事故分 */
  private async doPublishDefective(projectId: string): Promise<void> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    assertStatus(project, 'developing', '残次品上架')
    if (!readArtifact(projectId, 'index-html')?.trim()) {
      throw new GmError('MISSING_ARTIFACT', '连残次品都没有——只能再派一轮或放弃了。')
    }
    const projects = bundle.projects.projects.map((p) =>
      p.id === projectId
        ? { ...p, output_path: `games/${projectId}/index.html`, status: 'in_review' as const }
        : p,
    )
    await this.writeBatchSafe(projectId, { projects: { projects } })
    audit(projectId, 'projects', `${projectId}.status → in_review`, '老板拍板：残次品上架（评审按事故锚点打分）')
    removeArtifact(projectId, 'last-failure') // 事故情报一次性：送审即消费
    gateEvent(projectId, 'accident-triage@on-failure', 'exit')
    narrate(projectId, '制作人助理', '残次品已送审。市场不会嘴下留情，但这也是经营的一部分。')
    gateEvent(projectId, 'publish-confirm@pre-review', 'enter')
  }

  // ── §5+§6 上架（in_review → completed）──────────────────────────────────

  private async doPublish(projectId: string): Promise<SettleResult> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    assertStatus(project, 'in_review', '上架')
    const html = readArtifact(projectId, 'index-html')
    if (!html?.trim()) {
      throw new GmError('MISSING_ARTIFACT', '产物区里找不到成品卡带，评审没法开场。')
    }
    const resolved = resolvedDecisionsOf(bundle, project)
    gateEvent(projectId, 'publish-confirm@pre-review', 'exit')

    // ① 冒烟台架（降级试玩协议第一级，报告注入评审 prompt 作证据）
    emitPhase(projectId, 'review', '沙箱冒烟 + 送审中')
    const smokeReport = await this.smoke(html)

    // ② 评审（独立人格；同季度同一支笔）
    const penName = readPenName(bundle.market.season)
    let reviewOut
    try {
      reviewOut = await this.runner.runStage<ReviewSubscore>({
        projectId,
        stage: 'review',
        stageLabel: penName ?? '市场评审员',
        employeeId: 'reviewer',
        choice: REVIEWER_CHOICE,
        buildMessages: () =>
          buildReviewMessages(
            penName,
            project,
            bundle.market,
            resolved,
            html,
            JSON.stringify(smokeReport, null, 2),
          ),
        responseFormat: REVIEW_RESPONSE_FORMAT,
        parse: (raw) => validateReviewSub(parseStructured(raw), smokeReport.verdict),
      })
    } catch (err) {
      if (err instanceof ParseError) {
        throw new GmError(
          'REVIEW_INVALID',
          `评审员这回的稿子没法排版：${err.issues[0]}。要重派一次评审，还是改天再上架？`,
          err.issues,
        )
      }
      throw err
    }
    const sub = reviewOut.payload
    if (!penName && sub.pen_name.trim()) {
      writePenName(bundle.market.season, sub.pen_name.trim())
    }
    const byline = penName ?? sub.pen_name.trim() ?? '市场评审员'
    narrate(projectId, byline, '稿子写完了。分数？我自己看了都心疼。')
    audit(
      projectId,
      'reviewer',
      `评审 token ${reviewOut.usage.total_tokens}（市场侧开支，不计入公司三岗口径）`,
      '评审员用量快照',
    )

    // ③ 结算（分项 → 合并五数值字段 → 有序落盘；真实 token = 项目累计三岗 usage）
    emitPhase(projectId, 'settling', '结算盖章中')
    const tokenUsage = project.token_usage
    const review = buildReview(project, bundle.employees.employees, bundle.market, sub, tokenUsage.total)
    const result = await settle(this.store, null, { projectId, review, tokenUsage })

    // ④ GM 口吻结算简报（直播给订阅者；封盘后回放走全局段摘要）
    const lines = [
      `五维：完成 ${sub.scores.completion.score} / 创意 ${sub.scores.creativity.score} / 美术 ${sub.scores.art.score} / 手感 ${sub.scores.game_feel.score} / 题材 ${sub.scores.theme_fit.score}`,
      `加权总分 ${result.review.weighted_total}，收入 ${result.review.revenue} 金币，成本 ${result.review.cost}，盈亏 ${result.review.profit >= 0 ? '+' : ''}${result.review.profit}`,
      `经验 +${result.review.exp_gained}`,
    ]
    if (result.leveledTo) lines.push(`🎉 公司升级到 Lv.${result.leveledTo}！`)
    if (result.bailedOut) lines.push(`天使投资人第 ${result.saveAfter.bailout.times_used} 次擦屁股：资金补足到 ${result.saveAfter.bailout.grant_to}。`)
    lines.push('去「市场报告」看评审亲笔，去「作品图鉴」玩自己的游戏。')
    narrate(projectId, '游戏主控', lines.join('\n'))
    emitPhase(projectId, 'idle')

    // ⑤ 封盘清理（设计稿 §2.2.5 / §3.2）：会话删除；事件项目段摘要入全局后清理
    clearProjectSessions(projectId)
    archiveProjectSummary(
      projectId,
      `结算封盘：评审 ${review.id} 总分 ${review.weighted_total}，盈亏 ${review.profit >= 0 ? '+' : ''}${review.profit}，经验 +${review.exp_gained}`,
    )
    clearProjectEvents(projectId)
    return result
  }

  // ── §1.1 放弃（物理删除，无放弃态）───────────────────────────────────────

  private async doAbandon(projectId: string): Promise<void> {
    const bundle = await this.store.readAll()
    const project = findProject(bundle, projectId)
    if (project.status === 'completed') {
      throw new GmError('BAD_STATUS', '这盘卡带已经结算封盘了，没有放弃一说。')
    }
    const projects = bundle.projects.projects.filter((p) => p.id !== projectId)
    const decisions = bundle.decisions.decisions.filter((d) => d.project_id !== projectId)
    const reviews = bundle.reviews.reviews.filter((r) => r.project_id !== projectId)
    const teamIds = new Set([project.team.designer, project.team.programmer, project.team.artist])
    const employees = bundle.employees.employees.map((e) =>
      teamIds.has(e.id) && e.status === 'in_project' ? { ...e, status: 'hired' as const } : e,
    )
    await this.writeBatchSafe(projectId, {
      projects: { projects },
      decisions: { decisions },
      reviews: { reviews },
      employees: { employees },
    })
    clearArtifacts(projectId)
    audit(projectId, 'projects', `删除 ${projectId} 及其决策/评审`, '玩家放弃项目（物理删除）')
    audit(projectId, 'employees', '团队员工归还 hired', '放弃项目收尾')
    clearProjectSessions(projectId)
    archiveProjectSummary(projectId, '项目放弃：物理删除，立项成本打了水漂')
    clearProjectEvents(projectId)
    narrate(null, '游戏主控', '项目已撤销，立项成本打了水漂（不额外扣资金），可以开新项目了。')
  }

  // ── §6 恢复协议：四类物证按序重估（全部只读存档与持久键，不依赖内存态）─────

  async resume(): Promise<ResumeReport> {
    // ⓪ 脏结算标记优先：回滚 save 快照后幂等重放，杜绝双倍结算
    const pending = readPendingSettlement()
    if (pending) {
      narrate(null, '游戏主控', '上次结算盖到一半的章，我这就补完。')
      const result = await settle(this.store, pending)
      clearPendingSettlement()
      return {
        settlementResumed: result,
        project: null,
        action: 'settlement-resumed',
        message: `结算补完：盈亏 ${result.review.profit >= 0 ? '+' : ''}${result.review.profit}，经验 +${result.review.exp_gained}`,
      }
    }

    // ② 唤醒队列卡住请求重估（claimed 崩溃卡死 / deferred）
    await this.reevaluateStuck()

    // ③ active 会话 → 对应动作续跑（coalescing 与 ④ 的入队天然去重）
    for (const projectId of this.projectsWithActiveSessions()) {
      const bundle = await this.store.readAll()
      const project = bundle.projects.projects.find((p) => p.id === projectId)
      if (!project || project.status === 'completed') continue
      const action = this.actionForProject(project)
      if (action) {
        try {
          await this.enqueue(action, projectId, 'resume')
        } catch (err) {
          console.warn('[gm] 恢复路径续跑失败（不阻断后续恢复）：', err)
        }
      }
    }

    // ④ 按项目 status 给断点提示（继承旧版 resume 语义）
    const bundle = await this.store.readAll()
    const actives = bundle.projects.projects
      .filter((p) => p.status !== 'completed')
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
    const project = actives[0] ?? null
    if (!project) {
      return { settlementResumed: null, project: null, action: 'none', message: '没有在研项目。' }
    }
    switch (project.status) {
      case 'drafting':
        return { settlementResumed: null, project, action: 'wait-start', message: '存档读到了——上次立项后还没开工，去项目面板点「开工」。' }
      case 'awaiting_decision':
        return { settlementResumed: null, project, action: 'await-decision', message: '存档读到了——有决策请求等你拍板，去项目面板点「拍板」定夺。' }
      case 'developing': {
        const html = readArtifact(project.id, 'index-html')
        if (!html?.trim()) {
          return { settlementResumed: null, project, action: 'continue-dev', message: '存档读到了——开发进行到一半，去项目面板点「开始开发」继续。' }
        }
        try {
          await this.enqueue('accept', project.id, 'system')
        } catch (err) {
          console.warn('[gm] 恢复路径补验收失败：', err)
        }
        return { settlementResumed: null, project, action: 'accepted', message: '存档读到了——成品已在库，已自动补验收，可以上架了。' }
      }
      case 'in_review': {
        // 先查 reviews 是否已有该 project_id 的评审：有 → 直接补跑结算（防重复评审双记录）
        const review = bundle.reviews.reviews.find((r) => r.project_id === project.id)
        if (review) {
          const marker: PendingSettlement = {
            projectId: project.id,
            review,
            tokenUsage: project.token_usage,
            saveBefore: bundle.save,
            createdAt: new Date().toISOString(),
          }
          const result = await settle(this.store, marker)
          return {
            settlementResumed: result,
            project: null,
            action: 'settlement-resumed',
            message: '存档读到了——上次评审已落盘但结算没走完，已补完。',
          }
        }
        return { settlementResumed: null, project, action: 'wait-publish', message: '存档读到了——成品验收已过，去项目面板点「上架」送审。' }
      }
      case 'completed':
        return { settlementResumed: null, project: null, action: 'none', message: '没有在研项目。' }
    }
  }

  /** 有 active 会话的项目 id 列表（恢复物证 ③） */
  private projectsWithActiveSessions(): string[] {
    const out = new Set<string>()
    for (const project of this.knownProjectIds()) {
      for (const stage of STAGES) {
        const s = loadSession(project, stage)
        if (s && s.state === 'active') out.add(project)
      }
    }
    return [...out]
  }

  private knownProjectIds(): string[] {
    // 会话键前缀扫描：tp3:session:<projectId>:<stage>
    const ids: string[] = []
    try {
      const s = typeof localStorage === 'undefined' ? null : localStorage
      if (!s) return ids
      for (let i = 0; i < s.length; i++) {
        const k = s.key(i)
        const m = k?.match(/^tp3:session:([^:]+):/)
        if (m) ids.push(m[1])
      }
    } catch {
      // 忽略
    }
    return [...new Set(ids)]
  }

  /** 按项目当前 status 选恢复动作（resume 源；coalescing 防重） */
  private actionForProject(project: Project): WakeupAction | null {
    switch (project.status) {
      case 'drafting':
        return 'startPlanning'
      case 'developing':
        return 'startDevelopment'
      case 'in_review':
        return 'publish'
      default:
        return null
    }
  }

  // ── 运行状态查询（设计稿 §8.3：getRunState）──────────────────────────────

  getRunState(projectId: string): RunStateInfo {
    const events = replayEvents(projectId)
    let phase: GmPhase = 'idle'
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i]
      if (e.kind === 'phase') {
        phase = (e.payload.phase as GmPhase) ?? 'idle'
        break
      }
    }
    const queue = this.queue
      .list(projectId)
      .filter((r) => r.state === 'pending' || r.state === 'claimed' || r.state === 'deferred')
    let activeSession: RunStateInfo['activeSession'] = null
    for (const stage of STAGES) {
      const s = loadSession(projectId, stage)
      if (s && s.state === 'active') {
        activeSession = {
          stage: s.stage,
          rounds: s.rounds,
          usage: s.usage,
          state: s.state,
          partialChars: s.partialRaw?.length ?? 0,
        }
        break
      }
    }
    return { phase, queue, activeSession }
  }

  // ── 内部：落盘与审计 ─────────────────────────────────────────────────────

  /** 批量写 + 配额超限映射（主档写失败：明确的导出/清理引导） */
  private async writeBatchSafe(projectId: string, patch: Parameters<SaveStorePort['writeBatch']>[0]): Promise<void> {
    try {
      await this.store.writeBatch(patch)
    } catch (err) {
      if (!(err instanceof GmError) && isQuotaExceeded(err)) {
        throw new GmError(
          'STORAGE_FULL',
          '存档写不进去了（浏览器存储已满）——先去「电脑 → 系统设置」导出存档备份，再清理图鉴老作品或重置存档。',
          err,
        )
      }
      throw err
    }
  }

  private writeArtifactAudited(projectId: string, kind: ArtifactKind, content: string): void {
    const r = writeArtifact(projectId, kind, content)
    audit(projectId, `tp3:artifact:${projectId}:${kind}`, `${r.length} 字符`, `员工产出落盘（${kind}）`)
    if (r.nearCapacity || isNearCapacity()) {
      warn(projectId, '卡带架快塞满了——建议去「电脑 → 系统设置」导出存档，清理图鉴里的老作品。')
    }
  }

  private async patchTokenUsage(projectId: string, tokenUsage: TokenUsage): Promise<void> {
    const bundle = await this.store.readAll()
    const projects = bundle.projects.projects.map((p) =>
      p.id === projectId ? { ...p, token_usage: tokenUsage } : p,
    )
    await this.writeBatchSafe(projectId, { projects: { projects } })
    audit(projectId, 'projects', `${projectId}.token_usage 累计 ${tokenUsage.total}`, 'API 真实 usage 记账')
  }
}
